import { memo, MouseEvent, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useUnwindLogs } from '../../api/useUnwindLogs';
import { useUnwindLogsSocket } from '../../hooks/useUnwindLogsSocket';
import {
  UnwindLogCategory,
  UnwindLogEvent,
  UnwindLogLevel,
} from '../../types/unwind-logs.types';

const LEVEL_COLOR: Record<UnwindLogLevel, string> = {
  debug: '#9aa0a6',
  info: '#1e88e5',
  warn: '#f9a825',
  error: '#e53935',
};

const CATEGORY_COLOR: Record<UnwindLogCategory, string> = {
  ws: '#00897b',
  evaluation: '#3949ab',
  order: '#43a047',
  fill: '#7cb342',
  notification: '#ec407a',
  error: '#e53935',
  state: '#5e35b1',
  recovery: '#00acc1',
  skip: '#757575',
  rule: '#5e35b1',
  heartbeat: '#9e9e9e',
};

const LEVEL_RANK: Record<UnwindLogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

interface RuleLogsPanelProps {
  ruleId: string;
}

/**
 * Per-rule dev-logs panel — slots into the rule detail view (alongside the
 * ExecutionLogTable). Live tail at top via WebSocket, paginated history
 * loaded via `GET /rules/:ruleId/logs` below. Client-side filter by minimum
 * level (debug → error). Click a row to expand its payload.
 */
// Convert a `<input type="datetime-local">` string to epoch ms. Empty
// string → undefined so the filter is treated as "no bound on this side".
const parseLocal = (s: string): number | undefined => {
  if (!s) return undefined;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : undefined;
};

const ALL_CATEGORIES: UnwindLogCategory[] = [
  'ws',
  'evaluation',
  'order',
  'fill',
  'notification',
  'error',
  'state',
  'recovery',
  'skip',
  'rule',
  'heartbeat',
];

/**
 * Hard ceilings to keep rendering cheap. The socket ring already
 * trims to MAX_RING (500) on its side; on top of that we never let
 * the DOM hold more than MAX_VISIBLE_ROWS or merge more than
 * MAX_MERGED, so a fresh scroll back to the top of the panel never
 * triggers a 1000+-element reconciliation. Older entries remain
 * reachable through the paginated history fetch ("Load more").
 */
const MAX_VISIBLE_ROWS = 250;
const MAX_MERGED = 600;

/** Module-level <style> for the row CSS. Injected once per page, not
 *  per-mount, so reopening the drawer does not pay for stylesheet
 *  setup. The class names are unique-prefixed to avoid leaking into
 *  unrelated MUI markup. */
const ROW_STYLES = `
.ulrow{
  border-bottom:1px solid rgba(255,255,255,0.04);
  padding:4px 8px;
  font-family:ui-monospace,SFMono-Regular,monospace;
  font-size:0.72rem;
  color:#e8e8e8;
  content-visibility:auto;
  contain-intrinsic-size:0 28px;
}
.ulrow:hover{background:rgba(255,255,255,0.03)}
.ulrow-line{display:flex;align-items:center;gap:8px;line-height:1.2}
.ulrow-toggle{width:14px;color:#9aa0a6;font-size:0.7rem;text-align:center;flex-shrink:0}
.ulrow-ts{width:110px;color:#9aa0a6;flex-shrink:0}
.ulrow-cat{font-size:0.62rem;color:#fff;padding:1px 6px;border-radius:8px;flex-shrink:0;line-height:1.4}
.ulrow-lvl{width:8px;height:8px;border-radius:50%;flex-shrink:0;display:inline-block}
.ulrow-platform{width:70px;color:#9aa0a6;flex-shrink:0}
.ulrow-event{width:180px;color:#bdbdbd;flex-shrink:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ulrow-msg{flex:1;color:#f5f5f5;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ulrow-copy{background:transparent;border:none;color:#9aa0a6;cursor:pointer;padding:0 4px;font-size:14px;flex-shrink:0}
.ulrow-copy:hover{color:#fff}
.ulrow-payload{
  margin:4px 0 0 32px;padding:8px;background:rgba(0,0,0,0.3);
  border-radius:4px;color:#cfcfcf;white-space:pre;overflow-x:auto;
  font-size:0.7rem;
}
`;
let stylesInjected = false;
const ensureRowStyles = () => {
  if (stylesInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.setAttribute('data-rule-logs-panel', '1');
  el.textContent = ROW_STYLES;
  document.head.appendChild(el);
  stylesInjected = true;
};

interface LogRowProps {
  ev: UnwindLogEvent;
  isOpen: boolean;
  onToggle: (key: string) => void;
}

/**
 * Plain-DOM row. Was an MUI `<Stack>` of `<Chip>` / `<Typography>` /
 * `<IconButton>` — that's 8+ MUI components per row, which is what made
 * a 500-row live-tail feel sluggish. The MUI components add layout
 * thrash, ripple effects, and emotion `sx` evaluation per row. Plain
 * `<div>` / `<span>` is ~10x cheaper to render and reconcile.
 *
 * `contentVisibility: auto` lets the browser skip rendering offscreen
 * rows (Chrome/Edge feature) — critical when a few hundred rows scroll
 * past the viewport.
 */
const LogRow = memo(function LogRow({ ev, isOpen, onToggle }: LogRowProps) {
  const key = ev.id || `${ev.ts}:${ev.event}:${ev.message}`;
  const hasPayload = !!ev.payload && Object.keys(ev.payload).length > 0;
  const ts = new Date(ev.ts);
  const tsLabel = `${ts.toLocaleTimeString()}.${String(ts.getMilliseconds()).padStart(3, '0')}`;
  const catColor = CATEGORY_COLOR[ev.category] ?? '#666';
  const lvlColor = LEVEL_COLOR[ev.level] ?? '#666';

  const onCopy = (e: MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard.writeText(JSON.stringify(ev));
  };

  return (
    <div
      className="ulrow"
      style={{
        borderLeft: `3px solid ${catColor}`,
        cursor: hasPayload ? 'pointer' : 'default',
      }}
      onClick={hasPayload ? () => onToggle(key) : undefined}
    >
      <div className="ulrow-line">
        <span className="ulrow-toggle" style={{ visibility: hasPayload ? 'visible' : 'hidden' }}>
          {isOpen ? '▾' : '▸'}
        </span>
        <span className="ulrow-ts">{tsLabel}</span>
        <span className="ulrow-cat" style={{ background: catColor }}>
          {ev.category}
        </span>
        <span className="ulrow-lvl" style={{ background: lvlColor }} />
        <span className="ulrow-platform">{ev.platform || ''}</span>
        <span className="ulrow-event">{ev.event}</span>
        <span className="ulrow-msg">{ev.message}</span>
        <button className="ulrow-copy" onClick={onCopy} title="Copy event JSON">
          ⧉
        </button>
      </div>
      {isOpen && hasPayload && (
        <pre className="ulrow-payload">
          {JSON.stringify(ev.payload, null, 2)}
        </pre>
      )}
    </div>
  );
});

export const RuleLogsPanel = ({ ruleId }: RuleLogsPanelProps) => {
  ensureRowStyles();

  const [liveTail, setLiveTail] = useState(true);
  const [minLevel, setMinLevel] = useState<UnwindLogLevel>('debug');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [fromStr, setFromStr] = useState<string>('');
  const [toStr, setToStr] = useState<string>('');
  const [category, setCategory] = useState<UnwindLogCategory | 'all'>('all');

  const live = useUnwindLogsSocket({ ruleId, enabled: liveTail });
  const history = useUnwindLogs(ruleId, { enabled: !!ruleId });

  const merged = useMemo(() => {
    const seen = new Set<string>();
    const out: UnwindLogEvent[] = [];
    // Live (newest first) takes precedence.
    for (const ev of live.events) {
      const key = ev.id || `${ev.ts}:${ev.event}:${ev.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(ev);
      if (out.length >= MAX_MERGED) return out;
    }
    for (const page of history.data?.pages || []) {
      for (const ev of page.events) {
        const key = ev.id || `${ev.ts}:${ev.event}:${ev.message}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(ev);
        if (out.length >= MAX_MERGED) return out;
      }
    }
    return out;
  }, [live.events, history.data]);

  const fromTs = useMemo(() => parseLocal(fromStr), [fromStr]);
  const toTs = useMemo(() => parseLocal(toStr), [toStr]);

  const filtered = useMemo(() => {
    const minRank = LEVEL_RANK[minLevel];
    const out: UnwindLogEvent[] = [];
    for (const e of merged) {
      if (LEVEL_RANK[e.level] < minRank) continue;
      if (category !== 'all' && e.category !== category) continue;
      if (fromTs !== undefined && e.ts < fromTs) continue;
      if (toTs !== undefined && e.ts > toTs) continue;
      out.push(e);
      // Cap on the unfiltered tail. Filtering may drop most entries, in
      // which case we still want to show the matches even if they sit
      // beyond MAX_VISIBLE_ROWS. Only stop early when nothing has been
      // dropped (i.e. we're streaming the natural tail).
      if (
        category === 'all' &&
        minRank === 0 &&
        fromTs === undefined &&
        toTs === undefined &&
        out.length >= MAX_VISIBLE_ROWS
      ) {
        break;
      }
    }
    return out;
  }, [merged, minLevel, category, fromTs, toTs]);

  const toggleExpanded = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const hasTimeFilter = fromTs !== undefined || toTs !== undefined;
  const clearTime = () => {
    setFromStr('');
    setToStr('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.5, flexShrink: 0 }}>
        <Typography
          sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'grey.400' }}
        >
          Dev Logs
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.600' }}>
          {filtered.length}/{merged.length}
          {filtered.length >= MAX_VISIBLE_ROWS &&
          category === 'all' &&
          minLevel === 'debug' &&
          fromTs === undefined &&
          toTs === undefined
            ? ` (capped — filter to see more)`
            : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Switch
            size="small"
            checked={liveTail}
            onChange={(e) => setLiveTail(e.target.checked)}
          />
          <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
            Live{' '}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                ml: 0.5,
                bgcolor: live.isConnected ? '#43a047' : '#757575',
              }}
            />
          </Typography>
        </Stack>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <Select
            value={category}
            onChange={(e) =>
              setCategory(e.target.value as UnwindLogCategory | 'all')
            }
            sx={{ fontSize: '0.7rem', height: 24 }}
          >
            <MenuItem value="all">all categories</MenuItem>
            {ALL_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <Select
            value={minLevel}
            onChange={(e) => setMinLevel(e.target.value as UnwindLogLevel)}
            sx={{ fontSize: '0.7rem', height: 24 }}
          >
            <MenuItem value="debug">debug+</MenuItem>
            <MenuItem value="info">info+</MenuItem>
            <MenuItem value="warn">warn+</MenuItem>
            <MenuItem value="error">error</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 0.5, flexShrink: 0 }}
      >
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>
          From
        </Typography>
        <TextField
          type="datetime-local"
          size="small"
          value={fromStr}
          onChange={(e) => setFromStr(e.target.value)}
          inputProps={{ step: 1 }}
          sx={{
            '& .MuiInputBase-root': { height: 24, fontSize: '0.7rem' },
            '& input': { color: 'grey.300', py: 0 },
          }}
        />
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>
          To
        </Typography>
        <TextField
          type="datetime-local"
          size="small"
          value={toStr}
          onChange={(e) => setToStr(e.target.value)}
          inputProps={{ step: 1 }}
          sx={{
            '& .MuiInputBase-root': { height: 24, fontSize: '0.7rem' },
            '& input': { color: 'grey.300', py: 0 },
          }}
        />
        {hasTimeFilter && (
          <IconButton size="small" onClick={clearTime} title="Clear time range">
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
        <Box sx={{ flex: 1 }} />
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          bgcolor: 'rgba(0,0,0,0.2)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 1,
        }}
      >
        {filtered.length === 0 && (
          <Typography
            sx={{
              p: 2,
              fontSize: '0.72rem',
              color: 'grey.500',
              textAlign: 'center',
            }}
          >
            {history.isLoading
              ? 'Loading…'
              : 'No events yet. Logs appear here as the rule runs.'}
          </Typography>
        )}
        {filtered.map((ev) => {
          const key = ev.id || `${ev.ts}:${ev.event}:${ev.message}`;
          return (
            <LogRow
              key={key}
              ev={ev}
              isOpen={expanded.has(key)}
              onToggle={toggleExpanded}
            />
          );
        })}
      </Box>

      {history.hasNextPage && (
        <Box sx={{ mt: 0.5, textAlign: 'center', flexShrink: 0 }}>
          <Button
            size="small"
            onClick={() => history.fetchNextPage()}
            disabled={history.isFetchingNextPage}
            sx={{ fontSize: '0.7rem' }}
          >
            {history.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
